/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { WireModelDto } from '@treasury/api/channel';
import { NavigationService } from '@treasury/core/navigation';
import { TmDetailedError } from '@treasury/domain/shared';
import { UsersService } from '@treasury/domain/users';
import { WiresService } from '@treasury/domain/wires';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-list-header';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-status';
import { getStatusConfig } from '@treasury/presentation/components/tm-status';
import { type SummaryTileVm } from '@treasury/presentation/components/tm-summary-tile';
import { TmDialogButtonPosture, TmDialogService } from '@treasury/presentation/services/dialog';
import { ContextMenuItem } from '@vaadin/context-menu';
import '@vaadin/grid';
import { GridSelectedItemsChangedEvent } from '@vaadin/grid';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import '../../components/list-summary-bar';
import { WirePaymentTableViewModel, WirePaymentViewModel } from '../data/wire-payment-view-model';

export const tagName = 'wire-payments-table';
@customElement(tagName)
export class WirePaymentsTable extends TmContainer {
    @property({ type: Array })
    public wirePayments: WireModelDto[] = [];

    @property({ type: Boolean })
    public isRecurring = false;

    @property()
    public searchTerm = '';

    @property({ type: Array })
    public totals?: { totalAmount: number; count: number; status: string }[];

    @InjectProperty()
    declare private usersService: UsersService;

    @InjectProperty()
    declare private readonly wireService: WiresService;

    @InjectProperty()
    declare private readonly dialogService: TmDialogService;

    @state()
    private wirePaymentViewModels: WirePaymentViewModel[] = [];

    @state()
    private filteredWirePaymentViewModels: WirePaymentViewModel[] = [];

    @state()
    private selectedWires: WirePaymentViewModel[] = [];

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private fieldsToFilter = ['beneficiaryName', 'status', 'amount', 'debitAccount', 'paymentDate'];

    @state()
    private comments = '';

    @state()
    private selectedTile?: SummaryTileVm;

    public updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('wirePayments')) {
            this.buildWireViewModels();
        }
        if (changedProps.has('isRecurring')) {
            this.selectedTile = undefined;
            this.searchTerm = '';
            this.filterResults({ detail: { value: this.searchTerm } } as CustomEvent);
        }
    }

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div class="flex-none bg-primary">
                <tm-list-header
                    .filterButton=${true}
                    .searchTerm=${this.searchTerm}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-wire-payments'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                ></tm-list-header>
            </div>
            <div class="flex-grow">
                <div class="flex flex-col h-full">
                    <div class="flex-none">${this.renderTotals()}</div>
                    <div class="flex-grow">${this.renderTable()}</div>
                </div>
            </div>
        </div> `;
    }

    private buildWireViewModels() {
        this.wirePaymentViewModels = this.wirePayments.map(
            wirePayment =>
                new WirePaymentTableViewModel(
                    wirePayment as unknown as WireModelDto
                ) as WirePaymentViewModel
        );
        this.filteredWirePaymentViewModels = this.wirePaymentViewModels;
    }

    private filterResults(e: CustomEvent | string) {
        if (typeof e === 'string') {
            this.searchTerm = '';
        } else {
            this.searchTerm = e.detail.value;
        }
        this.filteredWirePaymentViewModels = filterTableResults(
            e,
            this.wirePaymentViewModels,
            this.fieldsToFilter
        );
    }

    private renderTotals() {
        if (
            !this.totals ||
            this.totals.length === 0 ||
            !this.isDesktop ||
            !this.wirePayments.length
        ) {
            return nothing;
        }
        return html`<list-summary-bar
            .summary=${this.totals}
            .selectedTile=${this.selectedTile}
            @tileClicked=${(e: CustomEvent) => {
                const status = e.detail.id;
                if (this.selectedTile?.title === status) {
                    this.filteredWirePaymentViewModels = this.wirePaymentViewModels;
                    this.selectedTile = undefined;
                    return;
                }
                this.selectedTile = e.detail;
                this.filterResults(status);
            }}
        ></list-summary-bar>`;
    }

    private renderStatusIconColumn(wirePayment: WirePaymentViewModel) {
        return html`<tm-status .status=${wirePayment.status} .label=${false}></tm-status>`;
    }

    private renderPaymentNameAndStatusColumn(wirePayment: WirePaymentViewModel) {
        return html`<div class="flex -ml-4">
            <div class="flex flex-col">
                <span class="payment-name text-sm font-medium">${wirePayment.beneficiaryName}</span>
                <small class="payment-transaction-id text-xs text-tertiary"
                    >From ${wirePayment.debitAccount}</small
                >
                <small
                    class="payment-status text-xs"
                    style=${`color: var(${getStatusConfig(wirePayment.status).color})`}
                    >${wirePayment.status}</small
                >
            </div>
        </div>`;
    }

    private renderAmountAndAccountColumn(wirePayment: WirePaymentViewModel) {
        return html`<div class="flex flex-col">
            <span class="payment-amount text-right text-sm font-medium">${wirePayment.amount}</span>
            <small class="payment-transaction-id text-right text-xs text-tertiary"
                >${wirePayment.frequencyDisplay}</small
            >
        </div>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.wirePayments.length) {
            return html`<div
                class=${classMap({
                    'w-full px-4 py-8 bg-primary text-center text-sm text-secondary': true,
                    'shadow-md': !this.isDesktop,
                })}
            >
                No wires found
            </div>`;
        }
        const isSelectable = (item: WirePaymentViewModel) => {
            return (
                item.status === 'Pending Approval' && item.permissions.some(p => p === 'Approve')
            );
        };
        return html` <vaadin-grid
            class=${classMap({
                'h-full': true,
                'shadow-md': !this.isDesktop,
            })}
            .items=${this.filteredWirePaymentViewModels}
            .isItemSelectable=${isSelectable}
            @active-item-changed=${({ detail }: CustomEvent) => {
                if (!detail.value) {
                    return;
                }
                this.dispatchEvent(new CustomEvent('wire-select', { detail: detail.value }));
            }}
            @selected-items-changed="${(e: GridSelectedItemsChangedEvent<WirePaymentViewModel>) => {
                this.selectedWires = e.detail.value;
                this.dispatchEvent(
                    new CustomEvent('selection-changed', {
                        detail: this.selectedWires,
                    })
                );
            }}"
        >
            ${this.renderColumns()}
        </vaadin-grid>`;
    }

    private renderColumns() {
        if (this.isDesktop) {
            return html`<vaadin-grid-selection-column frozen></vaadin-grid-selection-column
                ><vaadin-grid-sort-column
                    header="Transaction ID"
                    path="transactionId"
                    auto-width
                    flex-grow="1"
                    ${columnBodyRenderer(
                        (wire: WirePaymentTableViewModel) =>
                            html`<div class="flex items-center">
                                <tm-status .status=${wire.status} .label=${false}></tm-status>
                                <div class="flex flex-col pl-2">
                                    <span class="text-sm">${wire.transactionId}</span>
                                    <small
                                        class="payment-status text-xs"
                                        style=${`color: var(${getStatusConfig(wire.status).color})`}
                                        >${wire.status}</small
                                    >
                                </div>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Wire Company"
                    path="wireCompany.name"
                    auto-width
                    flex-grow="0"
                    ${columnBodyRenderer(
                        (wire: WirePaymentTableViewModel) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wire.wireCompany?.name}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Creditor Name"
                    path="beneficiaryName"
                    auto-width
                    flex-grow="0"
                    ${columnBodyRenderer(
                        (wire: WirePaymentTableViewModel) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wire.beneficiaryName}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Debit Account"
                    path="debitAccount"
                    auto-width
                    flex-grow="0"
                    ${columnBodyRenderer(
                        (wire: WirePaymentTableViewModel) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wire.wireCompany?.name}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Amount"
                    path="amount"
                    auto-width
                    flex-grow="0"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wire: WirePaymentTableViewModel) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm font-medium">${wire.amount} <small class="text-xs text-tertiary"
                                    >${wire.destinationCurrency}</small
                                >
                            </div></span>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                ${this.isRecurring
                    ? html`<vaadin-grid-sort-column
                              header="Frequency"
                              path="frequency"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.frequencyType}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="Created Date"
                              path="createdDate"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.createdDate}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="Next Payment Date"
                              path="nextPaymentDate"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.nextPaymentDate}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="End Date"
                              path="endDate"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.endDate}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>`
                    : nothing}
                ${!this.isRecurring
                    ? html`<vaadin-grid-sort-column
                              header="Effective Date"
                              path="paymentDate"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.paymentDate}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="Created Date"
                              path="createdDate"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.createdDate}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="Wire Type"
                              path="wireType"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.wireType}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>
                          <vaadin-grid-sort-column
                              header="Source"
                              path="source"
                              ${columnBodyRenderer(
                                  (wire: WirePaymentTableViewModel) =>
                                      html`<div class="flex flex-col">
                                          <span class="text-sm">${wire.source}</span>
                                      </div>`,
                                  []
                              )}
                          ></vaadin-grid-sort-column>`
                    : nothing}<vaadin-grid-sort-column
                    header="Status"
                    path="status"
                    ${columnBodyRenderer((wire: WirePaymentTableViewModel) => {
                        return html`<treasury-status-column
                            .item=${wire}
                            .completedApproversFunction=${async () =>
                                this.usersService.fetchCompletedApprovers({
                                    approvalEntity: 'wire',
                                    productId: wire.id as number | undefined,
                                    updatedBy: 0,
                                }) as unknown as string[]}
                            .eligibleApproversFunction=${async () =>
                                this.usersService.fetchAvailableApprovers({
                                    approvalEntity: 'wire',
                                    productId: wire.id as number | undefined,
                                    updatedBy: 0,
                                }) as unknown as string[]}
                        ></treasury-status-column>`;
                    })}
                ></vaadin-grid-sort-column
                ><vaadin-grid-column
                    frozen-to-end
                    flex-grow="0"
                    width="3rem"
                    text-align="end"
                    @click=${(e: Event) => e.stopPropagation()}
                    ${columnBodyRenderer((payment: WirePaymentViewModel) => {
                        const actions: Array<ContextMenuItem> = this.isRecurring
                            ? [{ text: 'Cancel Series' }]
                            : [{ text: 'Cancel' }];
                        if (
                            payment.status !== 'Cancelled' &&
                            payment.status !== 'Transmitted' &&
                            payment.status !== 'Posted' &&
                            payment.status !== 'Expired'
                        ) {
                            return html`<tm-actions-column
                                .items=${actions}
                                @action-selected=${() => {
                                    this.cancelWirePayment(payment);
                                }}
                            ></tm-actions-column>`;
                        }
                        return nothing;
                    })}
                ></vaadin-grid-column>`;
        }
        return html`<vaadin-grid-column
                flex-grow="0"
                width="3.5rem"
                ${columnBodyRenderer(
                    this.renderStatusIconColumn as GridColumnBodyLitRenderer<WirePaymentViewModel>,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                flex-grow="1"
                ${columnBodyRenderer(
                    this
                        .renderPaymentNameAndStatusColumn as GridColumnBodyLitRenderer<WirePaymentViewModel>,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                ${columnBodyRenderer(
                    this
                        .renderAmountAndAccountColumn as GridColumnBodyLitRenderer<WirePaymentViewModel>,
                    []
                )}
            ></vaadin-grid-column>`;
    }

    private async cancelWirePayment(payment: WirePaymentViewModel) {
        const { reason } = await this.dialogService.open(
            html`<div>Are you sure you want to cancel this wire?</div>
                <br /><tm-text-field
                    label="Comments"
                    @value-changed=${(e: CustomEvent) => {
                        const { value } = e.detail;
                        this.comments = value;
                    }}
                ></tm-text-field>`,
            'Cancel Wire Payment',
            {
                buttons: {
                    [TmDialogButtonPosture.Primary]: {
                        label: 'Confirm',
                        reason: DynamicComponentExitReason.Confirm,
                    },
                    [TmDialogButtonPosture.Secondary]: {
                        label: 'Close',
                        reason: DynamicComponentExitReason.Cancel,
                    },
                },
            }
        );

        if (reason === DynamicComponentExitReason.Cancel) {
            return;
        }
        if (reason === DynamicComponentExitReason.Confirm) {
            this.loading = true;
            try {
                this.wireService.deleteWirePayment({
                    wireId: payment.id as number,
                    comments: this.comments,
                    id: 0,
                    updatedDate: '',
                });
                this.dispatchEvent(new CustomEvent('wire-payment-canceled', { detail: payment }));
                this.notificationService.renderSuccess('Wire payment canceled successfully');
            } catch (error) {
                if (error instanceof TmDetailedError) {
                    this.notificationService.renderError(error);
                } else if (error instanceof Error) {
                    this.notificationService.renderError(error);
                } else {
                    this.notificationService.renderError('Error decisioning wire payment.');
                }
            } finally {
                this.loading = false;
            }
        }
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(body-row) {
                    height: 56px;
                }
                vaadin-grid::part(first-column-cell) {
                    border-top: none;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WirePaymentsTable;
    }
}
